import {
  AccountingTypeLabelModifier,
  accountingType,
} from '@/features/master-table/models';
import {
  type ReportOrganizingTableFormat2PolicesModel,
  format2ColumnType,
} from '@/features/mold/models/organizing-table';
import type { projectDetailUrlTypeEnum } from '@/features/mold/models/report';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { OpinionByAdministrativeReviewTeamLabelModifier } from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { createCsv, formatAmountReport } from '@/utils';
import type { z } from 'zod';

type HeaderKeyType = z.infer<typeof format2ColumnType>;

export const format2ColumnModifier = ({ year }: { year: number }) =>
  ({
    policyLevel1: t('policy-level-2'),
    policyLevel2: t('policy-level-3'),
    projectNumber: t('project-number'),
    name: t('project-name'),
    organization: t('organization-in-charge'),
    accountingType: t('accounting-type'),
    posteriorRequestedAmountTotal: `${t('fiscal-year-with-value', { year: year + 1 })}\n${t(
      'requested-amount',
    )}`,
    adminReformReviewOpinion: t('admin-reform-review-context'),
    projectDetailPageUrl: t('project-detail-page-url'),
  }) satisfies Record<HeaderKeyType, string>;

export const format2ValueConverter = {
  posteriorRequestedAmountTotalValue: ({
    value,
    type,
    negativeCount,
    isDebugMode,
    rawValue,
  }: {
    value: number | null | undefined;
    negativeCount: number | null | undefined;
    isDebugMode: boolean;
  } & (
    | { type: 'csv'; rawValue?: never }
    | { type: 'pdf'; rawValue: number | null | undefined }
  )) => {
    return formatAmountReport(value, type, {
      negativeCount,
      isDebugMode,
      rawValue: type === 'pdf' ? rawValue : undefined,
    });
  },
  adminReformReviewOpinion: (value: string | null) => {
    return value ? OpinionByAdministrativeReviewTeamLabelModifier[value] : '';
  },
  accountingType: (
    type: z.infer<typeof accountingType> | null | undefined,
    accountTitle: string | undefined | null,
  ) => {
    if (!type) {
      return '';
    }
    return (
      (type === 'special'
        ? accountTitle
        : AccountingTypeLabelModifier[accountingType.Values.general]) ?? ''
    );
  },
};

export const getFormat2CsvData = ({
  year,
  policies,
  isDebugMode,
  projectDetailUrlType,
}: {
  year: number;
  policies: Array<z.infer<typeof ReportOrganizingTableFormat2PolicesModel>>;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}) => {
  const csvFormattedReports = policies.flatMap<
    Record<HeaderKeyType, string | number>
  >((policyLevel1) => {
    return policyLevel1.policyMeasuresData.flatMap((policyLevel2) => {
      return policyLevel2.projectsData.flatMap((p) => ({
        policyLevel1: policyLevel1.policyName ?? t('not-set'),
        policyLevel2: policyLevel2.measureName ?? t('not-set'),
        projectNumber: p.projectNumber ?? '',
        name: p.name ?? '',
        adminReformReviewOpinion:
          format2ValueConverter.adminReformReviewOpinion(
            p.adminReformReviewOpinion,
          ),
        posteriorRequestedAmountTotal:
          format2ValueConverter.posteriorRequestedAmountTotalValue({
            value: p.posteriorRequestedAmountTotal,
            type: 'csv',
            negativeCount: p.negativeRequestedAmountCount,
            isDebugMode,
          }),
        organization: p.organization ?? '',
        accountingType: format2ValueConverter.accountingType(
          p.accountingType,
          p.accountTitle,
        ),
        projectDetailPageUrl: getProjectDetailPageUrl({
          isPS: projectDetailUrlType === 'public',
          projectId: p.id,
        }),
      }));
    });
  });

  return createCsv({
    headerKeys: format2ColumnType.options,
    headerConverter: format2ColumnModifier({ year }),
    body: csvFormattedReports,
  });
};
